/**
 * Content Manager - Main Application Component
 *
 * This is the root component that sets up routing and theme.
 * Includes skip-to-content accessibility link and page transitions.
 */

import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Skeleton, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';

import { ThemeContextProvider } from './contexts/ThemeContext';
import Layout from './components/Layout';
import { useConfigStore } from './stores';
import { useAuthStore, selectHasHydrated } from './stores/authStore';

// =============================================================================
// LAZY-LOADED PAGES (Code splitting for better performance)
// =============================================================================
const ContentManagerApp = lazy(() =>
  import('./features/content/ContentManagerApp').then((m) => ({ default: m.ContentManagerApp }))
);
const SettingsPage = lazy(() => import('./pages/Settings'));
const LoginPage = lazy(() => import('./pages/Login'));
const GoogleOAuthCallback = lazy(() => import('./pages/GoogleOAuthCallback'));
const AzureOAuthCallback = lazy(() => import('./pages/AzureOAuthCallback'));
const AnalyticsPage = lazy(() => import('./pages/Analytics'));

// =============================================================================
// SKELETON PAGE LOADER (replaces CircularProgress spinner)
// =============================================================================
const PageLoader = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '50vh',
      gap: 3,
      p: 3,
      width: '100%',
      maxWidth: 800,
      mx: 'auto',
    }}
  >
    <Skeleton variant="rectangular" width="60%" height={32} sx={{ borderRadius: 1 }} />
    <Box sx={{ width: '100%', display: 'flex', gap: 2 }}>
      {[1, 2, 3, 4].map((i) => (
        <Skeleton key={i} variant="rectangular" sx={{ flex: 1, height: 80, borderRadius: 1 }} />
      ))}
    </Box>
    <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 1 }} />
    <Typography variant="body2" color="text.secondary">
      Loading...
    </Typography>
  </Box>
);

// =============================================================================
// PAGE TRANSITION WRAPPER
// =============================================================================
const AnimatedOutlet: React.FC = () => {
  const location = useLocation();

  return (
    <Box
      key={location.pathname}
      sx={{
        animation: 'fadeIn 200ms ease-in',
      }}
    >
      <Outlet />
    </Box>
  );
};

// =============================================================================
// PROTECTED ROUTE WRAPPER
// =============================================================================
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const hasHydrated = useAuthStore(selectHasHydrated);

  // Wait for auth store to hydrate from localStorage before making routing decisions
  if (!hasHydrated) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// =============================================================================
// MAIN APP COMPONENT
// =============================================================================
function App() {
  const fetchConfig = useConfigStore((state) => state.fetchConfig);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const hasHydrated = useAuthStore(selectHasHydrated);

  // Fetch global config from database only when authenticated
  // The /config endpoint requires authentication, so we must wait until the user is logged in
  useEffect(() => {
    if (isAuthenticated) {
      fetchConfig();
    }
  }, [fetchConfig, isAuthenticated]);

  return (
    <ThemeContextProvider>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
        <CssBaseline />
        {/* Skip to content link - first focusable element */}
        <a href="#main-content" className="skip-to-content">
          Skip to main content
        </a>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes */}
              <Route
                path="/login"
                element={
                  !hasHydrated ? (
                    <PageLoader />
                  ) : isAuthenticated ? (
                    <Navigate to="/" replace />
                  ) : (
                    <LoginPage />
                  )
                }
              />

              {/* OAuth callback routes (outside Layout) */}
              <Route path="/auth/google/callback" element={<GoogleOAuthCallback />} />
              <Route path="/auth/azure/callback" element={<AzureOAuthCallback />} />

              {/* Protected application routes with Layout */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route element={<AnimatedOutlet />}>
                  <Route index element={<ContentManagerApp />} />
                  <Route path="content" element={<ContentManagerApp />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="analytics" element={<AnalyticsPage />} />
                </Route>
              </Route>

              {/* Catch all - redirect to home (or login if not authenticated) */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </Router>
      </LocalizationProvider>
    </ThemeContextProvider>
  );
}

export default App;
